import { useCallback, useState } from 'react';

import { getAppointmentById } from '@/services/appointments';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppointmentItem } from '@/lib/getProjectAppointments';
import { CalendarClock, ChevronLeft, Home, SaveIcon } from 'lucide-react';
import dayjs from '@/lib/locale-dayjs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import ViewSignature from '@/components/view-signature';
import Signature from '@/components/signature-pad';
import SmsBtn from '@/components/sms-button';
import ImageUploader from '@/components/image-uploader';
import { queryClient } from '@/providers/query-provider';
import { Link, useParams } from 'react-router-dom';
import { Address, formatAddressUrl, formatTime } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from 'react-i18next';
import { captureException } from '@sentry/react';
import { CDN_URL, supabase } from '@/services';
import { useAuth } from '@/providers/auth-provider';
import ImageGallery from '@/components/image-gallery';
import { useUppyEvent } from '@uppy/react';
import { useUppy } from '@/providers/uppy-provider';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

type AppointmentData = AppointmentItem & {
  projects: ProjectTable & {
    users: {
      first_name: string;
      last_name: string;
      phone_number: string;
    };
  };
  images: ImageTable[];
};

export async function AppointmentLoader(appointmentId: string) {
  return queryClient.fetchQuery({
    queryKey: ['appointment', appointmentId],
    queryFn: () => getAppointmentById(appointmentId),
    staleTime: 1000,
  });
}

export const AppointmentPage = () => {
  const { appointmentId } = useParams();
  const { user } = useAuth();
  const { data, isLoading } = useQuery<AppointmentData>({
    networkMode: 'offlineFirst',
    queryKey: ['appointment', appointmentId],
    queryFn: async ({ queryKey }) => {
      const data = await getAppointmentById(queryKey[1] as string);
      return data;
    },
  });
  const { toast } = useToast();
  const { uppy } = useUppy();
  useUppyEvent(uppy, 'file-added', (f) => {
    f.meta = {
      ...f.meta,
      bucketName: import.meta.env.VITE_BUCKET_NAME,
      objectName: `${appointmentId}/${f.meta.name}`,
      contentType: f.type,
    };
  });
  useUppyEvent(uppy, 'error', (e) => {
    captureException(e);
  });

  const queryClient = useQueryClient();
  const [notes, setNotes] = useState(data?.notes ?? '');
  const [hasChanges, setHasChanges] = useState(false);

  const appointment_images = useMutation({
    mutationFn: async (records: ImageTable[]) => {
      // Create image table records.
      const { data, error } = await supabase.from('images').insert(records);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointment', appointmentId] });
    },
  });

  const notes_mutation = useMutation({
    mutationFn: async (notes: string) => {
      const { error, data } = await supabase.functions.invoke('notes', {
        body: JSON.stringify({
          appointment_id: appointmentId,
          notes,
        }),
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointment', appointmentId] });
      setHasChanges(false);
    },
    onError: () => {
      toast({
        title: 'Error',
        description: t('oops'),
        variant: 'destructive',
      });
    },
  });

  const delete_image = useMutation({
    mutationFn: async (image_id: string) => {
      // Delete image table records.
      const { data, error } = await supabase
        .from('images')
        .delete()
        .eq('image_id', image_id);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: t('imageDeleted'),
      });
      queryClient.invalidateQueries({ queryKey: ['appointment', appointmentId] });
    },
  });
  const { t } = useTranslation();

  const handleUpload = useCallback(
    (images: string[]) => {
      console.log(data);
      const records = images.map((image) => ({
        image_url: image,
        appointment_id: appointmentId,
        user_id: user?.id,
        project_id: data?.project_id,
      })) as ImageTable[];
      appointment_images.mutate(records);
    },
    [data, appointment_images, appointmentId, user?.id]
  );

  if (!data) {
    return <Spinner />;
  }

  const signatureImage = data.images?.find((image) => image.type === 'signature');
  const apptTime = formatTime(data?.appointment_time || '');
  const endTime = data?.end_date ? formatTime(data?.end_date) : '';
  function getBadgeClass(status: string) {
    switch (status) {
      case 'Scheduled':
        return 'bg-yellow-50 text-yellow-600 border-yellow-100';
      case 'Completed':
        return 'bg-green-50 text-green-600 border-green-100';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-100';
    }
  }

  function getClientInitials(row: AppointmentData) {
    const { projects } = row;
    if (!projects) return '';
    const { users } = projects;
    if (!users?.first_name) return '';
    return `${users?.first_name[0]}${users?.last_name[0]}`;
  }
  function getClientName(row: AppointmentData) {
    const { projects } = row;
    if (!projects) return '';
    const { users } = projects;
    if (!users?.first_name) return '';
    return `${users?.first_name} ${users?.last_name}`;
  }

  function getTimeFromNow(date: string = '') {
    return dayjs(date).fromNow();
  }
  const images = (data.images || [])
    .filter((e) => e.type === 'image')
    .map((e: ImageTable) => {
      return {
        ...e,
        image_url: `${CDN_URL}/object/public/${e.image_url}`,
      };
    });

  const handleDeleteImage = async (imageId: string) => {
    // Logic to delete an image
    // This should be an async function that deletes the image from your backend
    delete_image.mutate(imageId);
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setHasChanges(true);
    setNotes(e.target.value);
  };

  const saveNotes = async () => {
    notes_mutation.mutate(notes);
  };

  return (
    <>
      <div className="container flex h-14 items-center">
        <Link
          to="/"
          className="flex items-center text-sm font-medium text-muted-foreground"
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          {t('back')}
        </Link>
      </div>
      <Card className="max-w-full mx-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-7">
          <CardTitle className="text-md font-bold hidden md:block">
            {t('details')}
          </CardTitle>
          <div className="flex space-x-2 md:ml-auto justify-between w-full md:w-fit">
            <ImageUploader folderName={appointmentId!} onUploadComplete={handleUpload} />
            {data && data?.projects?.users?.phone_number && (
              <SmsBtn
                phoneNumber={data?.projects?.users?.phone_number}
                firstName={data?.projects?.users?.first_name}
                lastName={data?.projects?.users.last_name}
              />
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {isLoading && <Spinner />}

          <>
            <div className="flex justify-between space-x-4">
              <div className="flex gap-2 items-center">
                <Avatar className="h-12 w-12">
                  <AvatarImage src="/placeholder-avatar.jpg" alt={getClientName(data)} />
                  <AvatarFallback>{getClientInitials(data)}</AvatarFallback>
                </Avatar>
                <div>
                  <h2 className="text-md font-semibold">{getClientName(data)}</h2>
                  <p className="text-sm text-muted-foreground">{t('client')}</p>
                </div>
              </div>
              <div className="flex ml-auto items-center space-x-2">
                <Badge
                  variant="outline"
                  className={'text-md font-medium ' + getBadgeClass(data.status!)}
                >
                  {t(data.status?.toLowerCase().replace(/\s/g, '') ?? '')}
                </Badge>
              </div>
            </div>
            <div className="grid gap-4">
              <div className="">
                <h3 className="hidden md:block font-semibold">{t('startTime')}</h3>
                <CalendarClock className="h-5 w-5 self-start md:hidden " />
                <div className="flex gap-2 items-center">
                  <span className="flex flex-col">
                    <span className="text-sm">{apptTime}</span>
                    <span className="text-sm text-muted-foreground">
                      {getTimeFromNow(data?.appointment_time || '')}
                    </span>
                  </span>
                </div>
              </div>
              {endTime && (
                <div className="">
                  <h3 className="hidden md:block font-semibold">{t('endTime')}</h3>
                  <CalendarClock className="h-5 w-5 self-start md:hidden " />
                  <div className="flex gap-2 items-center">
                    <span className="flex flex-col">
                      <span className="text-sm">{endTime}</span>
                      <span className="text-sm text-muted-foreground">
                        {getTimeFromNow(data?.end_date || '')}
                      </span>
                    </span>
                  </div>
                </div>
              )}

              <div className="flex flex-col">
                <h3 className="hidden md:block font-semibold">{t('address')}</h3>
                <Home className="h-5 w-5  self-start md:hidden" />
                <div className="flex items-center gap-x-2">
                  <a
                    className="hover:underline text-sm flex items-center gap-2"
                    href={formatAddressUrl(data as Address)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <p>
                      {data.street}
                      <br />
                      {data.city}, {data.state}
                      <br /> {data.zip_code}
                    </p>
                  </a>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-2">{t('scope')}</h3>
              <p className="text-sm">{data.scope}</p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">{t('notes')}</h3>
              <Textarea
                placeholder={t('notes')}
                value={notes}
                onChange={handleNotesChange}
                readOnly={data.status === 'Completed'}
              />
            </div>
            <div className="flex justify-between w-full md:w-fit md:ml-auto gap-2">
              {images.length > 0 && (
                <ImageGallery images={images} onDeleteImage={handleDeleteImage} />
              )}
              <div className="ml-auto inline-flex gap-2">
                {hasChanges && (
                  <Button
                    type="button"
                    onClick={saveNotes}
                    disabled={notes_mutation.isPending}
                  >
                    {notes_mutation.isPending ? (
                      <Spinner className="h-4 w-4" />
                    ) : (
                      <SaveIcon className="h-4 w-4 mr-2" />
                    )}
                    {t('save')}
                  </Button>
                )}
                {signatureImage ? (
                  <ViewSignature appointmentId={appointmentId!} />
                ) : (
                  <Signature
                    project_id={data?.project_id ?? ''}
                    clientId={data.projects?.client_id ?? ''}
                    appointmentId={appointmentId ?? ''}
                  />
                )}
              </div>
            </div>
          </>
        </CardContent>
      </Card>
    </>
  );
};
